import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { officeOnboardingService } from "../../services/officeOnboarding.service";

export interface OfficeOnboardingStep1Data {
  officeName: string;
  officeAddress: string;
  officePhone: string;
  officeFax: string;
  province: string;
}

export interface OfficeOnboardingStep2Data {
  letterheadAdditionalText: string;
}

export interface OfficeOnboardingStep3Data {
  isPrimaryProvider: boolean | null;
  physicianFirstName?: string;
  physicianLastName?: string;
  physicianEmail?: string;
  medicalPractitionerNumber?: string;
  signatureFileId?: string;
  invitationSent?: boolean;
}

export interface OfficeOnboardingStep4Data {
  workNotePrice: number;
  prescriptionRefillPrice: number;
}

export interface OfficeOnboardingState {
  isModalOpen: boolean;
  currentStep: number;
  totalSteps: number;
  isCompleted: boolean;
  isLoading: boolean;
  error: string | null;
  step1Data: OfficeOnboardingStep1Data | null;
  step2Data: OfficeOnboardingStep2Data | null;
  step3Data: OfficeOnboardingStep3Data | null;
  step4Data: OfficeOnboardingStep4Data | null;
}

const initialState: OfficeOnboardingState = {
  isModalOpen: false,
  currentStep: 1,
  totalSteps: 5,
  isCompleted: false,
  isLoading: false,
  error: null,
  step1Data: null,
  step2Data: null,
  step3Data: null,
  step4Data: null,
};

// Async thunks for API calls
export const loadOnboardingStatus = createAsyncThunk("officeOnboarding/loadStatus", async (officeId: string | undefined, { rejectWithValue }) => {
  try {
    const status = await officeOnboardingService.getOnboardingStatus(officeId);
    return status;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const saveStep1Data = createAsyncThunk(
  "officeOnboarding/saveStep1",
  async ({ data, officeId }: { data: OfficeOnboardingStep1Data; officeId?: string }, { rejectWithValue }) => {
    try {
      const result = await officeOnboardingService.saveStep1(data, officeId);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const saveStep2Data = createAsyncThunk(
  "officeOnboarding/saveStep2",
  async ({ data, officeId }: { data: OfficeOnboardingStep2Data; officeId?: string }, { rejectWithValue }) => {
    try {
      const result = await officeOnboardingService.saveStep2(data, officeId);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const saveStep3Data = createAsyncThunk(
  "officeOnboarding/saveStep3",
  async ({ data, officeId }: { data: OfficeOnboardingStep3Data; officeId?: string }, { rejectWithValue }) => {
    try {
      const result = await officeOnboardingService.saveStep3(data, officeId);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const saveStep4Data = createAsyncThunk(
  "officeOnboarding/saveStep4",
  async ({ data, officeId }: { data: OfficeOnboardingStep4Data; officeId?: string }, { rejectWithValue }) => {
    try {
      const result = await officeOnboardingService.saveStep4(data, officeId);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const completeOnboarding = createAsyncThunk("officeOnboarding/complete", async (officeId: string | undefined, { rejectWithValue }) => {
  try {
    const result = await officeOnboardingService.completeOnboarding(officeId);
    return result;
  } catch (error) {
    return rejectWithValue(error);
  }
});

const officeOnboardingSlice = createSlice({
  name: "officeOnboarding",
  initialState,
  reducers: {
    openModal: state => {
      // Don't open modal if onboarding is complete (currentStep === 5 means all steps are done)
      if (state.isCompleted || state.currentStep === 5) {
        return;
      }
      state.isModalOpen = true;
    },
    closeModal: state => {
      state.isModalOpen = false;
    },
    setCurrentStep: (state, action: PayloadAction<number>) => {
      if (action.payload >= 0 && action.payload <= state.totalSteps) {
        state.currentStep = action.payload;
      }
    },
    goToNextStep: state => {
      if (state.currentStep < state.totalSteps) {
        state.currentStep += 1;
      }
    },
    goToPreviousStep: state => {
      if (state.currentStep > 1) {
        state.currentStep -= 1;
      }
    },
    setStep1Data: (state, action: PayloadAction<OfficeOnboardingStep1Data>) => {
      state.step1Data = action.payload;
    },
    setStep2Data: (state, action: PayloadAction<OfficeOnboardingStep2Data>) => {
      state.step2Data = action.payload;
    },
    setStep3Data: (state, action: PayloadAction<OfficeOnboardingStep3Data>) => {
      state.step3Data = action.payload;
    },
    setStep4Data: (state, action: PayloadAction<OfficeOnboardingStep4Data>) => {
      state.step4Data = action.payload;
    },
    resetOnboarding: state => {
      return initialState;
    },
  },
  extraReducers: builder => {
    builder
      // Load status
      .addCase(loadOnboardingStatus.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadOnboardingStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        // Use nullish coalescing (??) to allow 0 as a valid step (welcome screen)
        state.currentStep = action.payload.currentStep ?? 1;
        // If currentStep is 5, onboarding modal steps are complete (Steps 1-5 in OnboardingModal)
        // Note: bankCompleted & billingCompleted are separate flags for bank info and deposit account details
        state.isCompleted = action.payload.currentStep === 5 || action.payload.isCompleted || false;
        state.step1Data = action.payload.step1Data || null;
        state.step2Data = action.payload.step2Data || null;
        state.step3Data = action.payload.step3Data || null;
        state.step4Data = action.payload.step4Data || null;
      })
      .addCase(loadOnboardingStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Save Step 1
      .addCase(saveStep1Data.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(saveStep1Data.fulfilled, (state, action) => {
        state.isLoading = false;
        state.step1Data = action.payload;
      })
      .addCase(saveStep1Data.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Save Step 2
      .addCase(saveStep2Data.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(saveStep2Data.fulfilled, (state, action) => {
        state.isLoading = false;
        state.step2Data = action.payload;
      })
      .addCase(saveStep2Data.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Save Step 3
      .addCase(saveStep3Data.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(saveStep3Data.fulfilled, (state, action) => {
        state.isLoading = false;
        state.step3Data = action.payload;
      })
      .addCase(saveStep3Data.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Save Step 4
      .addCase(saveStep4Data.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(saveStep4Data.fulfilled, (state, action) => {
        state.isLoading = false;
        state.step4Data = action.payload;
      })
      .addCase(saveStep4Data.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Complete onboarding
      .addCase(completeOnboarding.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(completeOnboarding.fulfilled, state => {
        state.isLoading = false;
        state.isCompleted = true;
        // Don't close modal automatically - let user see Step 5 and close it manually
      })
      .addCase(completeOnboarding.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  openModal,
  closeModal,
  setCurrentStep,
  goToNextStep,
  goToPreviousStep,
  setStep1Data,
  setStep2Data,
  setStep3Data,
  setStep4Data,
  resetOnboarding,
} = officeOnboardingSlice.actions;

export default officeOnboardingSlice.reducer;
